import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { 
  ArrowLeft, FileText, Calendar, Truck, Users, Package, CheckCircle, Clock, 
  PauseCircle, XCircle, Loader2, Building, Phone, Mail, DollarSign, 
  Route, MapPin, Download, Eye, RefreshCw, AlertCircle, User, CreditCard,
  GitBranch, Shield, ShieldCheck, Sun, Moon, Settings2, Fuel, ClipboardCheck, CalendarDays, Download as DownloadIcon
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '../lib/utils';
import HierarchyView from '../components/features/HierarchyView';
import { toast } from 'sonner';
import { useClientPermissions } from '../hooks/useClientPermissions';
import { getVehicleTypeLabel } from '../constants/vehicleTypes';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorState } from '../components/common/ErrorState';
import ClientAttendanceCalendar from '../components/ClientAttendanceCalendar';
import ClientContractQuotesTab from '../components/contracts/ClientContractQuotesTab';
import ClientContractLoadingNotesTab from '../components/contracts/ClientContractLoadingNotesTab';
import ClientContractCalendarTab from '../components/contracts/ClientContractCalendarTab';
import ContractMonthlySummaryDialog from '../components/ContractMonthlySummaryDialog';
import { StatusLegendDrawer } from '../components/ui-kit/StatusBadge';
import { logger } from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configuration des statuts de contrat
const CONTRACT_STATUS_CONFIG = {
  draft: { label: 'Brouillon', color: 'bg-gray-100 text-gray-700 border-gray-300', icon: Clock },
  active: { label: 'Actif', color: 'bg-emerald-100 text-emerald-700 border-emerald-300', icon: CheckCircle },
  suspended: { label: 'Suspendu', color: 'bg-amber-100 text-amber-700 border-amber-300', icon: PauseCircle },
  closed: { label: 'Clôturé', color: 'bg-slate-100 text-slate-700 border-slate-300', icon: XCircle },
  cancelled: { label: 'Annulé', color: 'bg-red-100 text-red-700 border-red-300', icon: AlertCircle }
};

const TRANSFER_STATUS_CONFIG = {
  planifiee: { label: 'Planifié', color: 'bg-slate-100 text-slate-700', icon: Clock },
  planned: { label: 'Planifié', color: 'bg-slate-100 text-slate-700', icon: Clock },
  en_cours_de_chargement: { label: 'Chargement', color: 'bg-blue-100 text-blue-700', icon: Package },
  en_transit: { label: 'En transit', color: 'bg-amber-100 text-amber-700', icon: Route },
  en_cours_de_dechargement: { label: 'Déchargement', color: 'bg-purple-100 text-purple-700', icon: MapPin },
  in_progress: { label: 'En cours', color: 'bg-amber-100 text-amber-700', icon: Clock },
  completed: { label: 'Terminé', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  cancelled: { label: 'Annulé', color: 'bg-red-100 text-red-700', icon: XCircle }
};

const INVOICE_STATUS_CONFIG = {
  draft: { label: 'Brouillon', color: 'bg-gray-100 text-gray-700', icon: Clock },
  validated: { label: 'Validée', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  sent: { label: 'Envoyée', color: 'bg-purple-100 text-purple-700', icon: Mail },
  paid: { label: 'Payée', color: 'bg-emerald-100 text-emerald-700', icon: CreditCard },
  partially_paid: { label: 'Partiel', color: 'bg-amber-100 text-amber-700', icon: DollarSign },
  overdue: { label: 'En retard', color: 'bg-red-100 text-red-700', icon: AlertCircle }
};

const TERMINOLOGY_CONFIG = {
  transfert: { label: 'Transfert', plural: 'Transferts', gender: 'masculine' },
  operation: { label: 'Opération', plural: 'Opérations', gender: 'feminine' },
  course: { label: 'Course', plural: 'Courses', gender: 'feminine' }
};

const ContractStatusBadge = ({ status }) => {
  const config = CONTRACT_STATUS_CONFIG[status] || CONTRACT_STATUS_CONFIG.draft;
  const Icon = config.icon;
  return (
    <Badge className={cn('flex items-center gap-1.5 px-3 py-1.5 text-sm border', config.color)}>
      <Icon className="w-4 h-4" />
      {config.label}
    </Badge>
  );
};

const TransferStatusBadge = ({ status }) => {
  const config = TRANSFER_STATUS_CONFIG[status] || TRANSFER_STATUS_CONFIG.planned;
  const Icon = config.icon;
  return (
    <Badge className={cn('flex items-center gap-1 px-2 py-0.5 text-xs', config.color)}>
      <Icon className="w-3 h-3" />
      {config.label}
    </Badge>
  );
};

const InvoiceStatusBadge = ({ status }) => {
  const config = INVOICE_STATUS_CONFIG[status] || INVOICE_STATUS_CONFIG.draft;
  const Icon = config.icon;
  return (
    <Badge className={cn('flex items-center gap-1 px-2 py-0.5 text-xs', config.color)}>
      <Icon className="w-3 h-3" />
      {config.label}
    </Badge>
  );
};

const ClientContractDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const permissions = useClientPermissions();
  
  const [contract, setContract] = useState(null);
  const [kpi, setKpi] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [runs, setRuns] = useState([]);
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  const fetchContract = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/contracts/${id}`);
      setContract(response.data);
      
      // Fetch KPI
      try {
        const kpiResponse = await axios.get(`${API}/contracts/${id}/kpi`);
        setKpi(kpiResponse.data);
      } catch (e) {
        logger.debug('KPI not available');
      }
    } catch (error) {
      console.error('Error fetching contract:', error);
      toast.error('Erreur lors du chargement du contrat');
      navigate('/mes-contrats');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchRuns = useCallback(async () => {
    if (!id) return;
    setLoadingRuns(true);
    try {
      const response = await axios.get(`${API}/contract-runs?contract_id=${id}&limit=100`);
      setRuns(response.data.items || response.data || []);
    } catch (error) {
      console.error('Error fetching runs:', error);
    } finally {
      setLoadingRuns(false);
    }
  }, [id]);

  const fetchInvoices = useCallback(async () => {
    if (!id) return;
    setLoadingInvoices(true);
    try {
      const response = await axios.get(`${API}/contract-invoices/by-contract/${id}`);
      setInvoices(response.data.items || response.data || []);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoadingInvoices(false);
    }
  }, [id]);

  useEffect(() => {
    fetchContract();
  }, [fetchContract]);

  useEffect(() => {
    if (contract) {
      fetchRuns();
      fetchInvoices();
    }
  }, [contract, fetchRuns, fetchInvoices]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR');
  };

  const formatPrice = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA';
  };

  const handleDownloadDocument = async () => {
    try {
      const response = await axios.get(`${API}/contracts/${id}/document`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', contract?.signedDocument?.fileName || 'contrat.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Impossible de télécharger le document');
    }
  };

  const handleDownloadCertifiedInvoice = async (invoiceId, invoiceNumber) => {
    try {
      const response = await axios.get(`${API}/fne/pdf/contract/${invoiceId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Facture_certifiee_${invoiceNumber || invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Facture certifiée téléchargée');
    } catch (error) {
      console.error('Download certified error:', error);
      toast.error('Erreur lors du téléchargement de la facture certifiée');
    }
  };

  const handleDownloadInvoice = async (invoiceId) => {
    try {
      const response = await axios.get(`${API}/contract-invoices/${invoiceId}/download`, { 
        responseType: 'blob',
        headers: {
          'Authorization': 'Bearer cookie-auth'
        }
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `facture_${invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Facture téléchargée');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Erreur lors du téléchargement de la facture');
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!contract) {
    return (
      <ErrorState
        title="Contrat non trouvé"
        onRetry={() => navigate('/mes-contrats')}
      />
    );
  }

  const terminologyLabel = TERMINOLOGY_CONFIG[contract.terminology]?.plural || 'Opérations';
  const terminologySingular = TERMINOLOGY_CONFIG[contract.terminology]?.label || 'Opération';
  const isMasculine = TERMINOLOGY_CONFIG[contract.terminology]?.gender === 'masculine';

  return (
    <div className="space-y-6" data-testid="client-contract-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/mes-contrats')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-lg md:text-2xl font-bold text-gray-900">
                {contract.contractNumber}
              </h1>
              <ContractStatusBadge status={contract.status} />
            </div>
            <p className="text-gray-500 text-sm">Contrat de location mensuelle</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <StatusLegendDrawer />
          <ContractMonthlySummaryDialog
            contractId={contract.id}
            contractNumber={contract.contractNumber}
          />
          <Button variant="outline" onClick={fetchContract}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      {kpi && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Package className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">{terminologyLabel}</p>
                  <p className="text-2xl font-bold">{kpi.runsTotal || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Taux de service</p>
                  <p className="text-2xl font-bold">{kpi.serviceRate || 0}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <DollarSign className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Facturé</p>
                  <p className="text-xl font-bold">{formatPrice(kpi.totalInvoiced)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">À payer</p>
                  <p className="text-xl font-bold text-amber-600">{formatPrice(kpi.totalUnpaid)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Vue d'ensemble</TabsTrigger>
          <TabsTrigger value="operations">{terminologyLabel}</TabsTrigger>
          <TabsTrigger value="hierarchy" data-testid="tab-hierarchy">
            <GitBranch className="w-4 h-4 mr-1" />
            Vue Hiérarchique
          </TabsTrigger>
          {permissions?.canViewFinances !== false && <TabsTrigger value="invoices">Factures</TabsTrigger>}
          <TabsTrigger value="quotes" data-testid="tab-quotes">
            <FileText className="w-4 h-4 mr-1" />
            Devis
          </TabsTrigger>
          <TabsTrigger value="loading-notes" data-testid="tab-loading-notes">
            <ClipboardCheck className="w-4 h-4 mr-1" />
            Bons
          </TabsTrigger>
          <TabsTrigger value="calendar" data-testid="tab-calendar">
            <CalendarDays className="w-4 h-4 mr-1" />
            Calendrier
          </TabsTrigger>
          <TabsTrigger value="fleet" data-testid="tab-fleet">
            <Truck className="w-4 h-4 mr-1" />
            Flotte & Service
          </TabsTrigger>
          <TabsTrigger value="attendance" data-testid="tab-attendance">
            <Calendar className="w-4 h-4 mr-1" />
            Présence
          </TabsTrigger>
        </TabsList>

        {/* Vue d'ensemble */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Période du contrat */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Calendar className="w-5 h-5 text-gray-400" />
                  Période du contrat
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Date de début</p>
                    <p className="font-medium">{formatDate(contract.startDate)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Date de fin</p>
                    <p className="font-medium">{formatDate(contract.endDate)}</p>
                  </div>
                </div>
                
                {/* Durée calculée */}
                {contract.startDate && contract.endDate && (
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-700">
                      Durée totale: {Math.ceil((new Date(contract.endDate) - new Date(contract.startDate)) / (1000 * 60 * 60 * 24 * 30))} mois
                    </p>
                  </div>
                )}
                
                <div className="pt-4 border-t">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Renouvellement automatique</p>
                      <p className="font-medium">{contract.autoRenew ? 'Oui' : 'Non'}</p>
                    </div>
                    <Badge variant="outline">
                      {terminologyLabel}
                    </Badge>
                  </div>
                </div>
                
                {/* Fréquence de facturation */}
                {contract.billingRules?.billingCycle && (
                  <div className="pt-4 border-t">
                    <p className="text-sm text-gray-500">Fréquence de facturation</p>
                    <p className="font-medium capitalize">{contract.billingRules.billingCycle}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Informations financières */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <DollarSign className="w-5 h-5 text-gray-400" />
                  Informations financières
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {contract.billingRules?.pricingModel === 'monthly_forfait' ? (
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-gray-500">Forfait mensuel</p>
                      <p className="text-2xl font-bold text-gray-900">
                        {formatPrice(contract.billingRules?.monthlyForfait)}
                      </p>
                    </div>
                    {contract.billingRules?.includedRuns && (
                      <div className="p-3 bg-emerald-50 rounded-lg">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-emerald-700">{terminologyLabel} inclus</span>
                          <span className="font-bold text-emerald-700">{contract.billingRules.includedRuns}</span>
                        </div>
                      </div>
                    )}
                    {contract.billingRules?.extraRunPrice && (
                      <div>
                        <p className="text-sm text-gray-500">Tarif par unité supplémentaire</p>
                        <p className="font-medium">{formatPrice(contract.billingRules.extraRunPrice)}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <p className="text-sm text-gray-500">Tarif par unité</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {formatPrice(contract.billingRules?.pricePerRun)}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">
                      par {TERMINOLOGY_CONFIG[contract.terminology]?.label?.toLowerCase() || 'opération'}
                    </p>
                  </div>
                )}
                
                {/* TVA */}
                <div className="pt-3 border-t space-y-2">
                  {contract.billingRules?.applyTVA ? (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500">TVA applicable</span>
                      <Badge variant="outline">{contract.billingRules?.tvaRate || 18}%</Badge>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">TVA non applicable</p>
                  )}
                </div>
                
                {/* Conditions de paiement */}
                {contract.billingRules?.paymentTermDays && (
                  <div className="pt-3 border-t">
                    <p className="text-sm text-gray-500">Délai de paiement</p>
                    <p className="font-medium">{contract.billingRules.paymentTermDays} jours</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Document signé */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <FileText className="w-5 h-5 text-gray-400" />
                  Document contractuel
                </CardTitle>
              </CardHeader>
              <CardContent>
                {contract.signedDocument ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <FileText className="w-8 h-8 text-emerald-600" />
                      <div className="flex-1">
                        <p className="font-medium text-emerald-800">{contract.signedDocument.fileName}</p>
                        {contract.signedDocument.signedDate && (
                          <p className="text-sm text-emerald-600">
                            Signé le {formatDate(contract.signedDocument.signedDate)}
                          </p>
                        )}
                      </div>
                    </div>
                    <Button variant="outline" onClick={handleDownloadDocument} className="w-full">
                      <Download className="w-4 h-4 mr-2" />
                      Télécharger le contrat
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <FileText className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                    <p className="text-gray-500">Aucun document disponible</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Transporteur */}
            {contract.transportPartnerName && (
              <Card data-testid="client-transport-partner-card">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                      <Truck className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{contract.transportPartnerName}</p>
                      <p className="text-xs text-emerald-600">Transporteur assigne</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Ressources dédiées */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Truck className="w-5 h-5 text-gray-400" />
                  Ressources dédiées
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Véhicules */}
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    Véhicules ({contract.dedicatedVehicles?.length || 0})
                  </p>
                  {contract.dedicatedVehicles?.length > 0 ? (
                    <div className="space-y-2">
                      {contract.dedicatedVehicles.map((vehicle, idx) => (
                        <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                          <Truck className="w-4 h-4 text-gray-500" />
                          <span className="font-mono text-sm">{vehicle.plate}</span>
                          {vehicle.type && (
                            <Badge variant="outline" className="text-xs">{vehicle.type}</Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400">Aucun véhicule dédié</p>
                  )}
                </div>

                {/* Chauffeurs */}
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    Chauffeurs ({contract.dedicatedDrivers?.length || 0})
                  </p>
                  {contract.dedicatedDrivers?.length > 0 ? (
                    <div className="space-y-2">
                      {contract.dedicatedDrivers.map((driver, idx) => (
                        <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                          <User className="w-4 h-4 text-gray-500" />
                          <span className="text-sm">{driver.name}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400">Aucun chauffeur dédié</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Notes et conditions - Full width */}
          {(contract.operationalDetails?.description || contract.operationalDetails?.specialConditions || contract.notes) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <FileText className="w-5 h-5 text-gray-400" />
                  Notes et conditions
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {contract.operationalDetails?.description && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1">Description</p>
                    <p className="text-sm text-gray-600 whitespace-pre-wrap">{contract.operationalDetails.description}</p>
                  </div>
                )}
                {contract.notes && (
                  <div className="pt-3 border-t">
                    <p className="text-sm font-medium text-gray-700 mb-1">Notes</p>
                    <p className="text-sm text-gray-600 whitespace-pre-wrap">{contract.notes}</p>
                  </div>
                )}
                {contract.operationalDetails?.specialConditions && (
                  <div className="pt-3 border-t">
                    <p className="text-sm font-medium text-gray-700 mb-1">Conditions spéciales</p>
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-sm text-amber-800 whitespace-pre-wrap">{contract.operationalDetails.specialConditions}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Contact */}
          {contract.assignedManager && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Building className="w-5 h-5 text-gray-400" />
                  Votre gestionnaire de compte
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <User className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{contract.assignedManager.name || 'Non assigné'}</p>
                    {contract.assignedManager.email && (
                      <div className="flex items-center gap-1 text-sm text-gray-500">
                        <Mail className="w-3 h-3" />
                        <a href={`mailto:${contract.assignedManager.email}`} className="hover:text-red-600">
                          {contract.assignedManager.email}
                        </a>
                      </div>
                    )}
                    {contract.assignedManager.phone && (
                      <div className="flex items-center gap-1 text-sm text-gray-500">
                        <Phone className="w-3 h-3" />
                        <a href={`tel:${contract.assignedManager.phone}`} className="hover:text-red-600">
                          {contract.assignedManager.phone}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Vos interlocuteurs (Operateurs assignes) */}
          {(contract.assignedOperators || []).length > 0 && (
            <Card data-testid="client-operators-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Users className="w-5 h-5 text-gray-400" />
                  Vos interlocuteurs
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {contract.assignedOperators.map((op, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                        <User className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">{op.name}</p>
                        {op.email && (
                          <a href={`mailto:${op.email}`} className="text-xs text-gray-500 hover:text-blue-600 flex items-center gap-1">
                            <Mail className="w-3 h-3" />{op.email}
                          </a>
                        )}
                        {op.phone && (
                          <a href={`tel:${op.phone}`} className="text-xs text-gray-500 hover:text-blue-600 flex items-center gap-1">
                            <Phone className="w-3 h-3" />{op.phone}
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Contacts d'escalade */}
          {contract.escalationContacts && (contract.escalationContacts.level1?.name || contract.escalationContacts.level2?.name || contract.escalationContacts.level3?.name) && (
            <Card data-testid="client-escalation-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Phone className="w-5 h-5 text-gray-400" />
                  Contacts d'escalade
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[
                    { key: 'level1', label: 'Niveau 1 - Contact initial', color: 'border-l-emerald-500' },
                    { key: 'level2', label: 'Niveau 2 - Escalade', color: 'border-l-amber-500' },
                    { key: 'level3', label: 'Niveau 3 - Direction', color: 'border-l-red-500' }
                  ].map(({ key, label, color }) => {
                    const c = contract.escalationContacts?.[key];
                    if (!c?.name) return null;
                    return (
                      <div key={key} className={`border-l-4 ${color} p-3 bg-gray-50 rounded-r-lg`}>
                        <p className="text-[10px] font-semibold text-gray-400 uppercase mb-1">{label}</p>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="font-medium text-gray-800">{c.name}</span>
                          {c.role && <span className="text-gray-500">({c.role})</span>}
                          {c.email && (
                            <a href={`mailto:${c.email}`} className="text-gray-600 hover:text-blue-600 flex items-center gap-1">
                              <Mail className="w-3 h-3" />{c.email}
                            </a>
                          )}
                          {c.phone && (
                            <a href={`tel:${c.phone}`} className="text-gray-600 hover:text-blue-600 flex items-center gap-1">
                              <Phone className="w-3 h-3" />{c.phone}
                            </a>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        <TabsContent value="operations" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <Package className="w-5 h-5 text-gray-400" />
                {terminologyLabel} ({runs.length})
              </CardTitle>
              <Button variant="outline" size="sm" onClick={fetchRuns} disabled={loadingRuns}>
                <RefreshCw className={cn("w-4 h-4", loadingRuns && "animate-spin")} />
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              {loadingRuns ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : runs.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500">{isMasculine ? 'Aucun' : 'Aucune'} {terminologySingular.toLowerCase()} pour ce contrat</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Référence</TableHead>
                      <TableHead>Trajet</TableHead>
                      <TableHead className="w-[80px] text-right">Distance</TableHead>
                      <TableHead>PTAC</TableHead>
                      <TableHead>Poids net</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead>Véhicule</TableHead>
                      <TableHead>Chauffeur</TableHead>
                      <TableHead className="w-[80px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {runs.map((run) => (
                      <TableRow key={run.id} data-testid={`run-row-${run.id}`}>
                        <TableCell>
                          <div className="text-sm">
                            <p className="font-medium">{formatDate(run.date)}</p>
                            <p className="text-xs text-gray-500">{run.shift === 'day' ? 'Jour' : 'Nuit'}</p>
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {run.runNumber || `#${run.id?.slice(0, 8)}`}
                        </TableCell>
                        <TableCell>
                          {run.stops?.length > 0 ? (() => {
                            const cities = run.stops
                              .map(s => s.city || s.address?.split(',')[0])
                              .filter(Boolean);
                            if (cities.length === 0) return <span className="text-gray-400 text-sm">-</span>;
                            const full = cities.join(' → ');
                            return (
                              <div
                                className="text-sm text-gray-700 truncate max-w-[240px]"
                                title={full}
                                data-testid={`client-run-${run.id}-trajet`}
                              >
                                {full}
                              </div>
                            );
                          })() : (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right text-sm font-medium text-gray-600" data-testid={`client-run-${run.id}-distance`}>
                          {run.distance_km ? `${Math.round(run.distance_km)} km` : <span className="text-gray-300">—</span>}
                        </TableCell>
                        <TableCell className="text-sm font-medium">
                          {run.weight ? `${run.weight} T` : '-'}
                        </TableCell>
                        <TableCell className="text-sm font-medium">
                          {run.netWeight ? `${run.netWeight} T` : '-'}
                        </TableCell>
                        <TableCell>
                          <TransferStatusBadge status={run.status} />
                        </TableCell>
                        <TableCell className="text-sm">
                          {run.plannedVehiclePlate || '-'}
                        </TableCell>
                        <TableCell className="text-sm">
                          {run.plannedDriverName || '-'}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => navigate(`/mes-contrats/${id}/transferts/${run.id}`)}
                            title="Voir le détail"
                            data-testid={`view-run-${run.id}`}
                          >
                            <Eye className="w-4 h-4 text-gray-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Vue Hiérarchique */}
        <TabsContent value="hierarchy" className="space-y-4" data-testid="hierarchy-tab-content">
          <HierarchyView 
            contractId={id} 
            contractNumber={contract.contractNumber}
            runs={runs}
            readOnly={true}
            onRefresh={fetchRuns}
          />
        </TabsContent>

        {/* Factures */}
        <TabsContent value="invoices" className="space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-lg">
                <CreditCard className="w-5 h-5 text-gray-400" />
                Factures ({invoices.length})
              </CardTitle>
              <Button variant="outline" size="sm" onClick={fetchInvoices} disabled={loadingInvoices}>
                <RefreshCw className={cn("w-4 h-4", loadingInvoices && "animate-spin")} />
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              {loadingInvoices ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : invoices.length === 0 ? (
                <div className="text-center py-12">
                  <CreditCard className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-500">Aucune facture pour ce contrat</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>N° Facture</TableHead>
                      <TableHead>Période</TableHead>
                      <TableHead>Opérations</TableHead>
                      <TableHead>Montant TTC</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead className="w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.map((invoice) => (
                      <TableRow key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                        <TableCell className="font-medium">
                          {invoice.invoiceNumber || `INV-${invoice.id?.slice(0, 8)}`}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p>{formatDate(invoice.periodStart)}</p>
                            <p className="text-xs text-gray-500">→ {formatDate(invoice.periodEnd)}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">
                          {invoice.runsCount || invoice.mainLines?.length || 0}
                        </TableCell>
                        <TableCell className="font-medium">
                          {formatPrice(invoice.totalTTC)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <InvoiceStatusBadge status={invoice.status} />
                            {invoice.fneCertification?.status === 'certified' && (
                              <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" data-testid={`fne-badge-${invoice.id}`} title={`Certifiée DGI — ${invoice.fneCertification?.reference || ''}`}>
                                <ShieldCheck className="w-3 h-3" /> FNE
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {['validated', 'sent', 'paid', 'partially_paid'].includes(invoice.status) && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDownloadInvoice(invoice.id)}
                                title="Télécharger la facture"
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                            )}
                            {invoice.fneCertification?.status === 'certified' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDownloadCertifiedInvoice(invoice.id, invoice.invoiceNumber)}
                                title="Télécharger la facture certifiée FNE"
                                data-testid={`fne-download-${invoice.id}`}
                              >
                                <ShieldCheck className="w-4 h-4 text-emerald-600" />
                              </Button>
                            )}
                            {invoice.fneCertification?.status === 'certified' && invoice.fneCertification?.token && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => window.open(invoice.fneCertification.token, '_blank')}
                                title="Vérifier la certification FNE (DGI)"
                                data-testid={`fne-verify-${invoice.id}`}
                              >
                                <Shield className="w-4 h-4 text-emerald-600" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Flotte & Service */}
        <TabsContent value="fleet" className="space-y-6">
          {/* Flotte mobilisée */}
          <Card data-testid="fleet-section">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Truck className="w-5 h-5 text-gray-400" />
                Flotte mobilisée
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(contract.billingRules?.fleetItems || []).length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {contract.billingRules.fleetItems.map((item, idx) => (
                    <div key={idx} className="p-4 bg-blue-50 border border-blue-100 rounded-xl" data-testid={`fleet-item-${idx}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Truck className="w-5 h-5 text-blue-600" />
                        <p className="font-medium text-blue-900">{item.vehicleTypeLabel || getVehicleTypeLabel(item.vehicleType) || 'Véhicule'}</p>
                      </div>
                      <p className="text-3xl font-bold text-blue-700">{item.quantity || 0}</p>
                      <p className="text-sm text-blue-600">véhicule{(item.quantity || 0) > 1 ? 's' : ''}</p>
                      {permissions.canViewPrices && item.monthlyRatePerTruck > 0 && (
                        <div className="mt-2 pt-2 border-t border-blue-200">
                          <p className="text-xs text-blue-500">Tarif mensuel / véhicule</p>
                          <p className="text-sm font-semibold text-blue-800">{new Intl.NumberFormat('fr-FR').format(item.monthlyRatePerTruck)} FCFA</p>
                        </div>
                      )}
                    </div>
                  ))}
                  {/* Total */}
                  <div className="p-4 bg-emerald-50 border border-emerald-100 rounded-xl flex flex-col justify-center">
                    <p className="text-sm text-emerald-600 mb-1">Total mobilisé</p>
                    <p className="text-3xl font-bold text-[#1db584]">
                      {contract.billingRules.fleetItems.reduce((sum, i) => sum + (i.quantity || 0), 0)}
                    </p>
                    <p className="text-sm text-emerald-700">véhicules</p>
                    {permissions.canViewPrices && (
                      <p className="text-xs text-emerald-500 mt-2">
                        Forfait total : {new Intl.NumberFormat('fr-FR').format(
                          contract.billingRules.fleetItems.reduce((sum, i) => sum + ((i.quantity || 0) * (i.monthlyRatePerTruck || 0)), 0)
                        )} FCFA/mois
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <Truck className="w-10 h-10 mx-auto mb-2" />
                  <p>Aucun véhicule défini pour ce contrat</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Véhicules dédiés */}
          {(contract.dedicatedVehicles || []).length > 0 && (
            <Card data-testid="dedicated-vehicles">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Shield className="w-5 h-5 text-gray-400" />
                  Véhicules dédiés
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {contract.dedicatedVehicles.map((v, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border">
                      <Truck className="w-5 h-5 text-gray-500" />
                      <div>
                        <p className="font-mono font-medium text-gray-900">{v.plate || v.licensePlate || v.immatriculation || 'N/A'}</p>
                        {(v.type || v.vehicleType) && <p className="text-xs text-gray-500">{v.type || getVehicleTypeLabel(v.vehicleType)}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Chauffeurs dédiés */}
          {(contract.dedicatedDrivers || []).length > 0 && (
            <Card data-testid="dedicated-drivers">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Users className="w-5 h-5 text-gray-400" />
                  Chauffeurs dédiés
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {contract.dedicatedDrivers.map((d, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border">
                      <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center">
                        <User className="w-4 h-4 text-emerald-700" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{d.name || d.driverName || `${d.firstName || ''} ${d.lastName || ''}`.trim() || 'N/A'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Planning de service */}
          {contract.serviceSchedule && (
            <Card data-testid="service-schedule">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Calendar className="w-5 h-5 text-gray-400" />
                  Planning de service
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Jours de service */}
                {contract.serviceSchedule.daysOfWeek && (
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-2">Jours de service</p>
                    <div className="flex gap-2">
                      {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((day, idx) => {
                        const dayKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
                        const isActive = contract.serviceSchedule.daysOfWeek?.includes(dayKeys[idx]) || contract.serviceSchedule.daysOfWeek?.includes(day.toLowerCase());
                        return (
                          <div key={day} className={cn(
                            "w-10 h-10 rounded-lg flex items-center justify-center text-sm font-medium border",
                            isActive ? "bg-[#1db584] text-white border-[#1db584]" : "bg-gray-50 text-gray-400 border-gray-200"
                          )}>
                            {day}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Horaires */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {contract.serviceSchedule.startTime && (
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500">Heure de début</p>
                      <p className="font-medium">{contract.serviceSchedule.startTime}</p>
                    </div>
                  )}
                  {contract.serviceSchedule.endTime && (
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500">Heure de fin</p>
                      <p className="font-medium">{contract.serviceSchedule.endTime}</p>
                    </div>
                  )}
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500">Service nuit</p>
                    <p className="font-medium flex items-center gap-1">
                      {contract.serviceSchedule.includesNight ? <><Moon className="w-3.5 h-3.5 text-indigo-500" /> Oui</> : <><Sun className="w-3.5 h-3.5 text-amber-500" /> Non</>}
                    </p>
                  </div>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500">Jours fériés</p>
                    <p className="font-medium">{contract.serviceSchedule.includesHolidays ? 'Inclus' : 'Exclus'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Facturation (hidden for client_logistique) */}
          {permissions.canViewFinances && contract.billingRules && (
            <Card data-testid="billing-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <DollarSign className="w-5 h-5 text-gray-400" />
                  Facturation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {contract.billingMode && (
                    <div className="p-4 bg-gray-50 rounded-xl">
                      <p className="text-xs text-gray-500 mb-1">Mode de facturation</p>
                      <p className="font-medium text-gray-900 capitalize">{contract.billingMode === 'forfait' ? 'Forfait mensuel' : contract.billingMode}</p>
                    </div>
                  )}
                  {contract.billingRules.monthlyForfait > 0 && (
                    <div className="p-4 bg-amber-50 border border-amber-100 rounded-xl">
                      <p className="text-xs text-amber-600 mb-1">Forfait mensuel total</p>
                      <p className="text-xl font-bold text-amber-800">{new Intl.NumberFormat('fr-FR').format(contract.billingRules.monthlyForfait)} FCFA</p>
                    </div>
                  )}
                  {contract.billingRules.fuelPolicy && (
                    <div className="p-4 bg-gray-50 rounded-xl">
                      <p className="text-xs text-gray-500 mb-1">Politique carburant</p>
                      <p className="font-medium text-gray-900">{contract.billingRules.fuelPolicy === 'included' ? 'Inclus dans le forfait' : (contract.billingRules.fuelPolicy === 'excluded' || contract.billingRules.fuelPolicy === 'client_charge') ? 'À la charge du client' : contract.billingRules.fuelPolicy === 'advance_with_receipts' ? 'Avance avec justificatifs' : contract.billingRules.fuelPolicy}</p>
                    </div>
                  )}
                  {contract.billingRules.pricePerUnit > 0 && (
                    <div className="p-4 bg-gray-50 rounded-xl">
                      <p className="text-xs text-gray-500 mb-1">Prix unitaire</p>
                      <p className="font-medium text-gray-900">{new Intl.NumberFormat('fr-FR').format(contract.billingRules.pricePerUnit)} FCFA</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Présence */}
        <TabsContent value="attendance" className="space-y-6">
          <ClientAttendanceCalendar contractId={id} />
        </TabsContent>

        <TabsContent value="quotes" className="space-y-4">
          <ClientContractQuotesTab contractId={id} />
        </TabsContent>

        <TabsContent value="loading-notes" className="space-y-4">
          <ClientContractLoadingNotesTab contractId={id} contract={contract} />
        </TabsContent>

        <TabsContent value="calendar" className="space-y-4">
          <ClientContractCalendarTab contractId={id} />
        </TabsContent>

      </Tabs>
    </div>
  );
};

export default ClientContractDetail;
